/**
 * Upload Portal Types and Interfaces
 *
 * Defines TypeScript types for the upload portal token structure,
 * file upload state, and API responses.
 */

export interface UploadTokenLimits {
  maxTotalBytes: number; // Default: 21474836480 (20GB)
  maxFileBytes?: number; // Optional: e.g. 10737418240 (10GB)
  allowedTypes: string[]; // MIME types or extensions
}

export interface UploadToken {
  tokenId: string;
  clientLabel: string; // Human-friendly: "Acme – Summer Camp 2026"
  driveFolderId: string;
  gcsPrefix: string; // e.g. "uploads/<tokenId>/"
  expiresAt: number; // Unix timestamp (now + 30 days)
  limits: UploadTokenLimits;
}

export type UploadFileStatus =
  | 'pending'
  | 'validating'
  | 'uploading'
  | 'complete'
  | 'error'
  | 'cancelled';

export interface UploadFile {
  id: string; // Unique ID for this file in the queue
  file: File;
  progress: number; // 0-100
  status: UploadFileStatus;
  error?: string;
  retryCount: number;
  sessionUrl?: string; // GCS resumable upload session URL
  signedUrl?: string; // GCS signed URL for direct upload
  uploadedBytes?: number; // Bytes uploaded so far (for resumable uploads)
  uploadMethod?: 'resumable' | 'signed'; // Which upload method to use
}

export interface UploadSession {
  sessionUrl: string;
  objectName: string; // Full GCS object path
  remainingQuota: number; // Bytes remaining for this token
}

export interface UploadStartResponse {
  success: boolean;
  sessionUrl?: string;
  signedUrl?: string; // Alternative to sessionUrl for CORS-free uploads
  objectName?: string;
  remainingQuota?: number;
  error?: string;
  allowedTypes?: string[];
  uploadMethod?: 'resumable' | 'signed'; // Indicates which upload method to use
}

export interface UploadStatusResponse {
  remainingQuota: number;
  totalUploaded: number;
  filesUploaded: number;
  recentUploads?: Array<{
    fileName: string;
    size: number;
    uploadedAt: string;
  }>;
}

/**
 * Parse and validate a JWT/HMAC token from the URL
 * @param tokenString - The token string to parse (JWT or base64-encoded JSON)
 * @returns Decoded token object or null if invalid
 */
export function parseToken(tokenString: string): UploadToken | null {
  try {
    // For now, we'll decode base64 JSON (mock mode)
    // In production, this will verify JWT signature
    const parts = tokenString.split('.');

    if (parts.length === 1) {
      // Assume it's a base64-encoded JSON (for mock mode)
      try {
        const decoded = JSON.parse(atob(tokenString));
        return decoded as UploadToken;
      } catch {
        return null;
      }
    } else if (parts.length === 3) {
      // JWT format: header.payload.signature
      const payload = JSON.parse(atob(parts[1]));
      return payload as UploadToken;
    }

    return null;
  } catch (error) {
    console.error('Token parsing error:', error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param token - The token to check
 * @returns True if token is expired, false otherwise
 */
export function isTokenExpired(token: UploadToken): boolean {
  return Date.now() >= token.expiresAt * 1000;
}

/**
 * Format bytes to human-readable string (e.g., "1.5 GB")
 * @param bytes - Number of bytes to format
 * @returns Formatted string with appropriate unit
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format bytes to GB with 2 decimal places
 * @param bytes - Number of bytes to format
 * @returns Formatted string in GB (e.g., "1.50")
 */
export function formatBytesToGB(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  return gb.toFixed(2);
}
